import React, { useEffect, useState } from 'react';
import TopBar from '../../Components/TopBar';
import Page from '../../Components/Containers/Page';
import { useSubscription, useLeads } from '../../Data/Marketplace';
import { PageLoading } from '../../Components/Loading';
import { getEntityLink, plural } from '../../Services/helpers';
import Badge from '../../Components/Badge/Badge';
import Services from '../../Components/Formatting/Services';
import Button from '../../Components/Button/Button';
import LeadDetails from './LeadDetails';
import { useDialogues } from '../../Components/Dialogues';
import { useNavigate } from 'react-router-dom';
import MyPatch from './MyPatch';
import { useSession } from '../../Components/Session';
import BusinessAddressRequired from './BusinessAddressRequired';
import Alert from '../../Components/Alert';
import LeadGeneration from './LeadGeneration';
import AddLeadCredits from '../Billing/AddLeadCredits';
import Notifications from './Notifications';

const Leads = () => {
    const {business} = useSession();
    const {leads, purchaseLead, isSaving: isPurchasing, refresh: leadsRefresh} = useLeads(50, true);
    const {subscription, isLeadGenActive, refresh: subscriptionRefresh} = useSubscription();
    const [leadOpen, setLeadOpen] = useState(false);
    const {confirm} = useDialogues();
    const navigate = useNavigate();
    const [editingPatch, setEditingPatch] = useState(false);
    const [leadGenModalOpen, setLeadGenModalOpen] = useState(false);
    const [addingLeads, setAddingLeads] = useState(false);
    const [notificationsOpen, setNotificationsOpen] = useState(false);

    useEffect(() => {
        if (subscription && subscription.area === null) {
            setEditingPatch(true);
        }
    }, [subscription]);

    const doPurchase = (lead) => {
        confirm(
            {
                theme: 'primary',
                title: 'Purchase lead',
                confirmLabel: 'Purchase',
                message: <>
                    Purchase this lead exclusively for {plural(lead.credits, null, '1 credit', `${lead.credits} credits`)}?<br />
                    You'll be able to view the customer's contact details immediately.
                </>
            },
            () => actuallyPurchase(lead)
        )
    }

    const actuallyPurchase = async (lead) => {
        await purchaseLead(lead.id).then((response) => {
            setLeadOpen(false);
            navigate(getEntityLink('Quotes/Quote', response.quoteId));
        });
    }

    return <>
        <TopBar title="Leads"
            dropdownOptions={[
                {
                    title: 'My patch',
                    onClick: () => setEditingPatch(true),
                    icon: 'DrawPolygon'
                },
                {
                    title: 'Notifications',
                    onClick: () => setNotificationsOpen(true),
                    icon: 'Bell'
                },
                {
                    title: 'Lead Generation',
                    onClick: () => setLeadGenModalOpen(true),
                    icon: 'RocketLaunch'
                },
                {
                    title: 'Add Lead Credits',
                    onClick: () => setAddingLeads(true),
                    icon: 'Plus'
                },
            ]}
        />

        <Page>
            {(leads === null || subscription === null || business === null) ?
                <PageLoading /> :
                <Inner {...{subscription, isLeadGenActive, leads, setLeadOpen, doPurchase, business, setEditingPatch, setLeadGenModalOpen}} />
            }
        </Page>

        <LeadDetails lead={leadOpen}
            doPurchase={actuallyPurchase}
            onClose={() => setLeadOpen(false)}
            isPurchasing={isPurchasing}
        />

        <MyPatch isOpen={editingPatch} onClose={() => setEditingPatch(false)} onSave={() => { subscriptionRefresh(); leadsRefresh(); }} />

        <Notifications isOpen={notificationsOpen} setIsOpen={() => setNotificationsOpen(false)} onSave={() => subscriptionRefresh()} />

        <LeadGeneration isOpen={leadGenModalOpen} setIsOpen={() => setLeadGenModalOpen(false)} onChange={subscriptionRefresh} />

        <AddLeadCredits isOpen={addingLeads} setIsOpen={setAddingLeads} onComplete={subscriptionRefresh} />
    </>
}

const Inner = ({subscription, isLeadGenActive, leads, setLeadOpen, doPurchase, business, setEditingPatch, setLeadGenModalOpen}) => {

    if (!business?.address?.streetName) {
        return <BusinessAddressRequired />
    }

    if (subscription.area === null) {
        return <div className="text-center mt-10 text-muted">
            <p>Set your patch to view nearby leads</p>
            <p>
                <Button theme="primary" label="Set my patch" onClick={() => setEditingPatch(true)} />
            </p>
        </div>
    }

    return <>
        {false && <Alert theme={(isLeadGenActive ? "success" : "warning")}
            className="mb-6 text-sm"
            title={"Lead generation: " + (isLeadGenActive ? "Enabled" : "Disabled")}
            message={
                isLeadGenActive ?
                "Any new leads in your patch will automatically be purchased for you." :
                "We're not currently generating any new leads for your patch. Tap for more info."
            }
            onClick={() => setLeadGenModalOpen(true)}
        />}

        {leads.length === 0 && <div className="mt-10 prose text-center text-muted">
            <p className="font-bold">No leads available</p>
            <p className="max-w-[80%] mx-auto">Leads will become available as other business in your area offer them in the marketplace.</p>
        </div>}

        <ul className="divide-y divide-gray-200">
            {leads.map(lead => {
                return <li key={lead.id} className="flex items-center justify-between gap-x-6 py-5">
                    <button className="w-full block grow" onClick={() => setLeadOpen(lead)}>
                        <div className="flex flex-col items-start gap-y-1">
                            <p className="font-bold">
                                {lead.firstName}
                                {lead.inPatch && <Badge theme="brand" title="My patch" className="ml-3" />}
                                {lead.sellerId === subscription.businessId && <Badge theme="danger" title="Selling" className="ml-3" />}
                            </p>
                            <p>
                                <Services services={lead.services} />
                                {/* <TimeAgo dateTime={lead.quoteSubmittedAt} />, &nbsp; */}
                                {/* {[lead.buildingType, lead.frequency].filter(x => !!x).join(', ')} */}
                                {/* {lead.frequency} */}
                            </p>
                            <p>
                                <span className="mr-3">{lead.postcode}</span>
                                {lead?.distance?.miles && <span className="text-muted">({lead.distance.miles} miles)</span>}
                            </p>
                            {/*
                            <p>
                                {lead.originalCredits && <span className="line-through text-muted mr-3">
                                    {plural(lead.originalCredits, null, '1 credit', `${lead.originalCredits} credits`)}
                                </span>}
                                {plural(lead.credits, null, '1 credit', `${lead.credits} credits`)}
                            </p>
                            */}
                        </div>
                    </button>
                    <div className="flex flex-none items-center gap-x-4 shrink-0">
                        <Button theme="primary" label="Purchase" onClick={() => doPurchase(lead)} />
                    </div>
                </li>
            })}
        </ul>
    </>
}


export default Leads;
